/**
 * Every section the user can reach, behind the ☰ Menu button.
 *
 * The top bar only carries the handful of primary tabs, which on the shell
 * this app replaces left roughly fifteen built pages — Examens, Notes,
 * Présence, RH, Frais, Reçus, Notifications, Annuaire — with no menu entry at
 * all, reachable only by typing the URL. That orphaning is what made the app
 * feel unfinished, and the drawer is what fixed it there.
 *
 * It lists only routes the user may actually open: the same filter the router
 * applies, so there are no dead links that land on "Accès non autorisé".
 * Grouping comes from each route's own `nav.group`, so a new screen appears
 * here by declaring where it belongs — nothing to keep in step by hand.
 */
import { useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { Icon, type IconName } from "@/components/ui";
import type { AppRoute } from "./routes";

/** Route group → the glyph beside its entries. A group with no entry here
 *  still renders; it just gets the neutral one. */
const GROUP_ICON: Record<string, IconName> = {
  Scolarité: "school",
  Finance: "payments",
  Personnel: "person",
  Administration: "settings",
  "Mon compte": "person",
};

export function NavDrawer({
  items, open, onClose,
}: {
  items: AppRoute[];
  open: boolean;
  onClose: () => void;
}) {
  const location = useLocation();
  const panelRef = useRef<HTMLElement>(null);

  // Escape closes, and so does a back-navigation: a drawer that survives the
  // back button is a drawer covering a page you thought you had returned to.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    window.addEventListener("popstate", onClose);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("popstate", onClose);
    };
  }, [open, onClose]);

  // Focus the first entry so the keyboard lands inside the panel rather than
  // continuing down the page behind it.
  useEffect(() => {
    if (!open) return;
    const first = panelRef.current?.querySelector<HTMLElement>("a[href]");
    const id = window.setTimeout(() => first?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  if (!open) return null;

  // Preserved insertion order: the caller already sorted by nav.order, so a
  // group appears where its first route does.
  const groups = new Map<string, AppRoute[]>();
  for (const route of items) {
    const group = route.nav!.group ?? "Établissement";
    groups.set(group, [...(groups.get(group) ?? []), route]);
  }

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-[970] flex bg-[rgba(15,23,42,.45)]"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <nav
        ref={panelRef}
        aria-label="Toutes les sections"
        className="flex h-[100dvh] w-[min(300px,84vw)] max-w-[300px] flex-col overflow-y-auto bg-surface shadow-[2px_0_24px_rgba(15,23,42,.25)]"
      >
        <div className="sticky top-0 flex items-center justify-between border-b border-border bg-surface px-4 py-3.5">
          <span className="text-base font-bold text-text">Menu</span>
          <button
            type="button"
            aria-label="Fermer"
            onClick={onClose}
            className="px-2 py-0.5 text-2xl leading-none text-text-muted"
          >
            ×
          </button>
        </div>

        {groups.size === 0 && (
          <p className="p-4 text-sm text-text-muted">Aucune section accessible.</p>
        )}

        {[...groups.entries()].map(([label, routes]) => (
          <div key={label}>
            <div className="px-4 pb-1 pt-3.5 text-[.72rem] font-bold uppercase tracking-wider text-text-faint">
              {label}
            </div>
            {routes.map((route) => {
              const active = location.pathname === route.path;
              return (
                <NavLink
                  key={route.path}
                  to={route.path}
                  onClick={onClose}
                  className={`flex min-h-[44px] w-full items-center gap-2.5 px-4 py-2.5 text-[.92rem] no-underline ${
                    active
                      ? "border-l-[3px] border-accent-600 bg-accent-soft font-semibold text-accent-600"
                      : "border-l-[3px] border-transparent text-text hover:bg-surface-muted"
                  }`}
                >
                  <span className={active ? "text-accent-600" : "text-text-faint"}>
                    <Icon name={GROUP_ICON[label] ?? "school"} size={18} />
                  </span>
                  {route.nav!.label}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>
    </div>
  );
}
